// mattermost-older-pages-end.ts
//
// Codex review CXR-07 (2026-10-01): when paging GET /channels/{id}/posts by
// `page`, page 0 holds the NEWEST posts and later pages go back in time.
// `next_post_id` names the post just NEWER than a page (always "" on page 0);
// `prev_post_id` names the post just OLDER — empty only when the channel has
// no older post (Mattermost v9.5.3 app/post.go AddCursorIdsForPostList +
// GetPrevPostIdFromPostList). Stopping on an empty `next_post_id` therefore
// read page 0 only. This is the single "walk back far enough" routine both
// pollers use.
import type { MattermostPost, MattermostPostList } from "./mattermost-rest-client.js";

/** Pages read before giving up (fail closed) — ~60k posts at 60 per page. */
export const MAX_POLL_PAGES = 1000;
// Review P2 M1: walks restarted because the history moved underneath them.
const MAX_WALK_RESTARTS = 3;

/**
 * True once no older page is needed: the channel has nothing older
 * (`prev_post_id` empty, or an empty page), or this page already reaches
 * back BEFORE `boundaryMs` (strictly — posts sharing the boundary
 * millisecond may continue on the next page).
 */
export function reachedOlderEndOfChannel(
  list: MattermostPostList,
  pagePosts: MattermostPost[],
  boundaryMs: number,
): boolean {
  if (!list.prev_post_id || pagePosts.length === 0) return true;
  const oldest = Math.min(...pagePosts.map((p) => p.create_at));
  return oldest < boundaryMs;
}

/** The page's posts in server order, skipping ids the map does not carry. */
export function postsOfPage(list: MattermostPostList): MattermostPost[] {
  return list.order.map((id) => list.posts[id]).filter((p): p is MattermostPost => p !== undefined);
}

/**
 * Reads pages 0, 1, … until one reaches `boundaryMs` (or the channel's
 * start). Page offsets are positional: a post deleted between two requests
 * shifts every later page by one, so the post just older than page k would
 * never be read (review P2 M1). Page k's `prev_post_id` must therefore be on
 * page k+1; when it is not, the walk starts over from page 0 (a bounded
 * number of times). Throws — never returns a partial history — when it
 * cannot finish, so a caller's cursor never advances past unread posts.
 */
export async function readPagesBackToBoundary(input: {
  fetchPage: (page: number) => Promise<MattermostPostList>;
  boundaryMs: number;
  maxPages?: number;
}): Promise<{ posts: MattermostPost[]; pagesRead: number }> {
  const maxPages = input.maxPages ?? MAX_POLL_PAGES;
  let pagesRead = 0;
  for (let walk = 0; walk <= MAX_WALK_RESTARTS; walk += 1) {
    const posts: MattermostPost[] = [];
    let olderPostExpected: string | undefined;
    for (let page = 0; ; page += 1) {
      if (page >= maxPages) {
        throw new Error(
          `Mattermost poll: đã đọc ${maxPages} trang mà chưa lùi tới mốc ${input.boundaryMs} — ` +
            "không tiến cursor (tránh bỏ sót directive cũ hơn).",
        );
      }
      const list = await input.fetchPage(page);
      pagesRead += 1;
      if (olderPostExpected !== undefined && !list.order.includes(olderPostExpected)) break;
      const pagePosts = postsOfPage(list);
      posts.push(...pagePosts);
      if (reachedOlderEndOfChannel(list, pagePosts, input.boundaryMs)) return { posts, pagesRead };
      olderPostExpected = list.prev_post_id;
    }
  }
  throw new Error(
    `Mattermost poll: lịch sử kênh đổi trong lúc đọc trang (${MAX_WALK_RESTARTS + 1} lần) — ` +
      "không tiến cursor, thử lại ở lần poll sau.",
  );
}
